/* eslint camelcase: 0 */ // fixing these poses a great risk at this point
import React, { useMemo } from 'react';
import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';
import Avatar, { AvatarUser } from '../general-components/avatar';
import type {
	ChatMessage,
	EventsMessage,
	EventsMessageImage,
	GeneralEventsMessage,
} from '../../stores/types/chat';
import { EMessageTypes } from '../../stores/types/chat';
import { getDisplayChatMessage } from '../../utils/utils';

interface ChatMessageProps {
	message: ChatMessage | GeneralEventsMessage;
}

function parseDate(date: string | Date): string {
	return new Date(date || Date.now()).toLocaleTimeString(navigator.language, { hour: 'numeric', minute: '2-digit' });
}

function getFirstAndLast(name: string) {
	const first_name = name.substr(0, name.indexOf(' '));
	const last_name = name.substr(name.indexOf(' ') + 1);

	return [first_name, last_name];
}

export default function ChatMessageRender(props: ChatMessageProps) {
	const { message } = props;

	function getUser(
		comm: ChatMessage | GeneralEventsMessage,
	): AvatarUser {
		const {
			first_name,
			last_name,
			email = ' ',
			avatar,
		} = comm;

		// Get name to cover legacy message edge cases
		const { name } = comm as { [key: string]: any };

		if (first_name && last_name) {
			return {
				first_name, last_name, email: email || ' ', avatar,
			};
		}

		if (name) {
			const [first, last] = getFirstAndLast(name);
			return {
				first_name: first, last_name: last, email: email || ' ', avatar,
			};
		}

		const generalEventsMessage = (comm as GeneralEventsMessage)?.message_type
			? comm as GeneralEventsMessage
			: null;

		let fullName = 'Anonymous';

		if (generalEventsMessage) {
			fullName = getDisplayChatMessage(generalEventsMessage).name;
		}

		return {
			first_name: fullName,
			last_name: ' ',
			email: ' ',
			avatar,
		};
	}

	function renderImage(image?: string | EventsMessageImage) {
		if (image) {
			if (typeof image === 'string') {
				if (image.endsWith('.mp4')) {
					return <video src={image} autoPlay muted={true} style={{ maxWidth: '100%' }} />;
				}
				return <img src={image} alt={''} />;
			} if (image.url) {
				return <img src={image.url} alt={''} />;
			}
			return <></>;
		}
		return <></>;
	}

	/*
	The following functions allow for easy mixing of different types of messages.
	It may not be an ideal solution, but it allows the current componenet to be
	used without too much modification or the duplication associated with a new
	component just for events messages.

	I'm not bothering with memoization because I don't think the added overhead
	would be worth it given how simple the functions are.
	*/
	function getCreatedDate() {
		const { created } = message as ChatMessage;
		const { created_at: createdAt } = message as EventsMessage;
		const createdAtDate = createdAt
			? new Date(createdAt)
			: created;

		return createdAtDate;
	}

	function getImage() {
		const { image } = message as ChatMessage;

		return image;
	}

	function getReplies() {
		const { replies } = message as ChatMessage;

		return replies ?? [];
	}

	// Setting css class to tell E3 message types apart for testing and future work
	const getEventsCssClasses = useMemo(() => {
		const { message_type: messageType } = message as EventsMessage;
		const commonCssClass = 'events-message';
		const eventsMessageTypes = new Map([
			[EMessageTypes.Chat, `${commonCssClass} chat-message`],
			[EMessageTypes.Question, `${commonCssClass} question`],
			[EMessageTypes.QuestionComment, `${commonCssClass} comment`],
		]);

		return eventsMessageTypes.get(messageType) ?? '';
	}, [message]);

	return (
		<div className="comment-container">
			<div className={classNames('chat-message-container', getEventsCssClasses)}>
				<Avatar
					user={getUser(message)}
					size="chat"
					noBorder={true}
				/>

				<div className={classNames('chat-message')}>
					<div className="chat-message-meta">
						<label>{getUser(message).first_name} {getUser(message).last_name}</label>
						<span>{parseDate(getCreatedDate())}</span>
					</div>

					<div className="chat-message-content">
						<>
							<ReactMarkdown
								components={{
									a: (componentProps: any) => <a href={componentProps.href} rel="noopener noreferrer" target="_blank">
											{componentProps.children}
										</a>,
								}}
							>
								{message.comment}
							</ReactMarkdown>
							{renderImage(getImage())}
						</>
					</div>
				</div>

			</div>
			{getReplies().map((reply: ChatMessage) => (
				<div key={reply.chat_message} className="chat-message-container reply">
					<Avatar
						user={getUser(reply)}
						size="chat"
						noBorder={true}
					/>

					<div className={classNames('chat-message')}>
						<div className="chat-message-meta">
							<label>{getUser(reply).first_name} {getUser(reply).last_name}</label>
							<span>{parseDate(reply.created)}</span>
						</div>

						<div className="chat-message-content">
							<ReactMarkdown
								components={{
									a: (componentProps: any) => <a href={componentProps.href} rel="noopener noreferrer" target="_blank">
											{componentProps.children}
										</a>,
								}}
							>
								{reply.comment}
							</ReactMarkdown>
						</div>
					</div>
				</div>
			))}
		</div>
	);
}
